import apiClient from './apiClient';

export interface App {
  id: number;
  appCode: string;
  appName: string;
  description?: string;
  logoUrl?: string;
  frontendUrl: string;
  backendUrl?: string;
  ssoCallbackUrl?: string;
  isActive: boolean;
  isPublic?: boolean;
  displayOrder: number;
  userCount?: number;
}

export interface SsoTokenResponse {
  token: string;
  redirectUrl: string;
  expiresIn: number;
  appCode: string;
  appName: string;
}

export interface CreateAppRequest {
  appCode: string;
  appName: string;
  description?: string;
  logoUrl?: string;
  frontendUrl: string;
  backendUrl?: string;
  ssoCallbackUrl: string;
  isPublic?: boolean;
  displayOrder?: number;
}

export const appService = {
  // Get all apps (admin view)
  async getAllApps(): Promise<App[]> {
    const response = await apiClient.get<{ success: boolean; data: App[] }>('/api/apps');
    return response.data.data || [];
  },

  // Get public apps (catalog view)
  async getPublicApps(): Promise<App[]> {
    const response = await apiClient.get<{ success: boolean; data: App[] }>('/api/apps/public');
    return response.data.data || [];
  },

  // Get app by ID
  async getAppById(appId: number): Promise<App> {
    const response = await apiClient.get<{ success: boolean; data: App }>(`/api/apps/${appId}`);
    return response.data.data;
  },

  // Get app by code
  async getAppByCode(appCode: string): Promise<App> {
    const response = await apiClient.get<{ success: boolean; data: App }>(`/api/apps/code/${appCode}`);
    return response.data.data;
  },

  // Create new app
  async createApp(data: CreateAppRequest): Promise<App> {
    const response = await apiClient.post<{ success: boolean; data: App }>(`/api/apps`, data);
    return response.data.data;
  },

  // Update app
  async updateApp(appId: number, data: CreateAppRequest): Promise<App> {
    const response = await apiClient.put<{ success: boolean; data: App }>(`/api/apps/${appId}`, data);
    return response.data.data;
  },

  // Delete app
  async deleteApp(appId: number): Promise<void> {
    await apiClient.delete<{ success: boolean; message: string }>(`/api/apps/${appId}`);
  },

  // Get user's accessible apps
  async getUserApps(): Promise<App[]> {
    const response = await apiClient.get<{ success: boolean; data: App[] }>(`/api/sso/user-apps`);
    return response.data.data || [];
  },

  // Generate SSO token for app
  async generateSsoToken(appCode: string): Promise<SsoTokenResponse> {
    const response = await apiClient.post<{ success: boolean; data: SsoTokenResponse }>(
      `/api/sso/generate-token`,
      { appCode }
    );
    return response.data.data;
  },

  // Grant user access to app
  async grantAccess(userId: number, appCode: string): Promise<void> {
    await apiClient.post<{ success: boolean; message: string }>(`/api/sso/grant-access`, { userId, appCode });
  },

  // Revoke user access from app
  async revokeAccess(userId: number, appCode: string): Promise<void> {
    await apiClient.delete<{ success: boolean; message: string }>(`/api/sso/revoke-access/${userId}/${appCode}`);
  },

  // Open app with SSO
  async openAppWithSSO(appCode: string): Promise<void> {
    try {
      const tokenResponse = await this.generateSsoToken(appCode);
      // Open in new tab
      window.open(tokenResponse.redirectUrl, '_blank');
    } catch (error) {
      console.error('Failed to open app with SSO:', error);
      throw error;
    }
  },
};

export default appService;
